import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logoImg from "./RoyalKingdoms.png";

const navItems = [
  { to: "/", label: { de: "Startseite", en: "Home" }, testid: "nav-home" },
  { to: "/welt", label: { de: "Die Welt", en: "The World" }, testid: "nav-world" },
  { to: "/charaktere", label: { de: "Charaktere", en: "Characters" }, testid: "nav-characters" },
  { to: "/system", label: { de: "OW · UW", en: "OB · UB" }, testid: "nav-system" }, 
  { to: "/buecher", label: { de: "Bücher", en: "Books" }, testid: "nav-books" },
  { to: "/test", label: { de: "Test", en: "Test" }, testid: "nav-test" }, 
  { to: "/shop", label: { de: "Shop", en: "Shop" }, testid: "nav-shop" },
];

export const Layout = ({ children }) => {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  const toggleLanguage = () => {
    const nextLang = currentLang === "de" ? "en" : "de";
    setCurrentLang(nextLang);
    localStorage.setItem("project_lang", nextLang);
    window.dispatchEvent(new Event("languageChange"));
  };

  return (
    <div className="relative min-h-screen">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isHome ? "bg-transparent" : "bg-[#080808]/80 backdrop-blur-xl"
        } border-b border-[#C9A227]/15`}
        data-testid="site-header"
      >
        <div className="w-full max-w-[1400px] mx-auto px-4 lg:px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="group flex items-center shrink-0" data-testid="logo-link">
            <img 
              src={logoImg} 
              alt="Royal Kingdoms Logo" 
              className="h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6 ml-auto">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `relative text-xs xl:text-sm tracking-[0.15em] xl:tracking-[0.2em] uppercase transition-colors duration-300 whitespace-nowrap ${
                    isActive ? "text-[#C9A227]" : "text-[#E8DDC8]/70 hover:text-[#E8DDC8]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {n.label[currentLang] || n.label["de"]}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-2 left-0 right-0 h-px bg-[#C9A227]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            
            {/* Sprachwechsler-Button */}
            <div className="pl-4 border-l border-[#C9A227]/20 shrink-0">
              <button
                onClick={toggleLanguage}
                className="px-2.5 py-1 text-[11px] font-mono tracking-wider border border-[#C9A227]/30 bg-[#0e0e0e] text-[#C9A227] hover:text-[#E8DDC8] hover:border-[#C9A227] transition-all rounded min-w-[35px] text-center"
              >
                {currentLang === "de" ? "EN/DE" : "DE/EN"}
              </button>
            </div>
          </nav>

          {/* Mobiler Menü-Button */}
          <button
            className="lg:hidden text-[#E8DDC8] shrink-0"
            onClick={() => setOpen(true)}
            aria-label="Menü öffnen"
            data-testid="menu-open-button"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#080808]/97 backdrop-blur-2xl flex flex-col"
            data-testid="mobile-drawer"
          >
            <div className="flex justify-between items-center px-6 py-5 border-b border-[#C9A227]/20">
              <span className="chinese text-[#C9A227] text-2xl">瑞华 </span>
              
              <div className="flex items-center gap-4">
                <button
                  onClick={toggleLanguage}
                  className="px-2.5 py-1 text-xs border border-[#C9A227]/30 bg-[#0e0e0e] text-[#C9A227] hover:text-[#E8DDC8] transition-all rounded"
                >
                  {currentLang === "de" ? "EN" : "DE"}
                </button>
                <button onClick={() => setOpen(false)} className="text-[#E8DDC8]" data-testid="menu-close-button">
                  <X size={26} />
                </button>
              </div>
            </div>
            
            <nav className="flex flex-col items-center justify-center flex-1 gap-7">
              {navItems.map((n, idx) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-${n.testid}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `serif text-3xl tracking-wider ${
                        isActive ? "text-[#C9A227]" : "text-[#E8DDC8]"
                      }`
                    }
                  >
                    {n.label[currentLang] || n.label["de"]}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 border-t border-[#C9A227]/15 mt-32 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <div className="ornament">◆ ◇ ◆</div>
          <p className="serif italic text-[#B3B6B7] text-lg">
            {currentLang === "en" 
              ? "„Turn your face to the sun and the shadows will fall behind you.“" 
              : "„Wende dein Gesicht der Sonne zu, dann fallen die Schatten hinter dich.“"}
          </p>
          <p className="text-xs text-[#7A7C7D] tracking-[0.3em] uppercase">
            {currentLang === "en"
              ? "© Royal Kingdoms — A world connected by the web of Elyra"
              : "© Royal Kingdoms — Eine Welt verbunden durch das Netz des Elyra"}
          </p>
          
          {/* Absolut positioniert: Der Link schwebt frei unter dem Footer, nimmt keinen Platz ein und lässt das Glitzer exakt an der Originallinie starten */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <Link
              to="/impressum"
              className="text-[10px] tracking-[0.25em] uppercase text-[#7A7C7D] hover:text-[#C9A227] transition-colors underline underline-offset-4 decoration-[#C9A227]/20"
            >
              {currentLang === "en" ? "Legal Notice" : "Impressum"}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};